import React, { useState, useEffect } from 'react';
import { Monitor } from '../types';

interface MonitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<void>;
  monitor?: Monitor | null;
  title: string;
}

const MonitorModal: React.FC<MonitorModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  monitor,
  title,
}) => {
  const [formData, setFormData] = useState<any>({
    name: '',
    url: '',
    type: 'static',
    interval: 60000,
    expected_status: 200,
    timeout: 10000,
    ssl_check_days: 7,
    headers: '',
    expected_content: '',
    content_type: 'text',
    port: undefined,
    host: '',
    priority: 'medium',
  });

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or monitor changes
  useEffect(() => {
    if (isOpen) {
      if (monitor) {
        // Edit mode - populate with existing data
        setFormData({
          name: monitor.name,
          url: monitor.url,
          type: monitor.type,
          interval: monitor.interval,
          expected_status: monitor.expected_status || 200,
          timeout: monitor.timeout || 10000,
          ssl_check_days: monitor.ssl_check_days || 7,
          headers: monitor.headers || '',
          expected_content: monitor.expected_content || '',
          content_type: monitor.content_type || 'text',
          port: monitor.port,
          host: monitor.host || '',
          priority: monitor.priority || 'medium',
        });
      } else {
        // Create mode - reset to defaults
        setFormData({
          name: '',
          url: '',
          type: 'static',
          interval: 60000,
          expected_status: 200,
          timeout: 10000,
          ssl_check_days: 7,
          headers: '',
          expected_content: '',
          content_type: 'text',
          port: undefined,
          host: '',
          priority: 'medium',
        });
      }
      setShowAdvancedOptions(false);
    }
  }, [isOpen, monitor]);

  // Set sensible defaults when type changes to supabase
  useEffect(() => {
    if (formData.type === 'supabase' && !monitor) {
      setFormData((prev: any) => ({
        ...prev,
        interval: 43200000, // 12 hours - well within the 7-day Supabase pause window
        timeout: 15000,     // 15 seconds - Supabase can be slow to cold-start
      }));
    }
  }, [formData.type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Failed to save monitor:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg mb-4">{title}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Monitor Name</span>
            </label>
            <input
              type="text"
              placeholder="My Website"
              className="input input-bordered"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-control">
            <a href="/docs/browser-monitor" target="_blank" rel="noreferrer" className="link link-primary">Read about Browser monitors</a>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">URL</span>
            </label>
            <input
              type="url"
              placeholder="https://example.com"
              className="input input-bordered"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              required
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Monitor Type</span>
              <span className="label-text-alt">Type of monitoring to perform</span>
            </label>
            <select
              className="select select-bordered"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
            >
              <option value="static">Static Ping (HTTP/HTTPS)</option>
              <option value="functional">Functional Test (HTTP + Content)</option>
              <option value="browser">Browser Render (full page render)</option>
              <option value="ssl">SSL Certificate</option>
              <option value="tcp">TCP Port</option>
              <option value="udp">UDP Port</option>
              <option value="dns">DNS Resolution</option>
              <option value="supabase">Supabase Keep-Alive</option>
            </select>
          </div>

          {/* Port Field (for TCP/UDP monitors) */}
          {(formData.type === 'tcp' || formData.type === 'udp') && (
            <div className="form-control">
              <label className="label">
                <span className="label-text">Port</span>
                <span className="label-text-alt">Port number to monitor (1-65535)</span>
              </label>
              <input
                type="number"
                className="input input-bordered"
                value={formData.port || ''}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || undefined })}
                placeholder="80"
                min="1"
                max="65535"
              />
            </div>
          )}

          {/* Host Field (for DNS monitors) */}
          {formData.type === 'dns' && (
            <div className="form-control">
              <label className="label">
                <span className="label-text">Host</span>
                <span className="label-text-alt">Hostname to resolve (optional, uses URL if not specified)</span>
              </label>
              <input
                type="text"
                className="input input-bordered"
                value={formData.host || ''}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                placeholder="example.com"
              />
            </div>
          )}

          {/* Supabase Keep-Alive Fields */}
          {formData.type === 'supabase' && (
            <>
              <div className="alert alert-info mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span>
                  Supabase pauses databases not touched for 7 days. This keep-alive monitor
                  periodically pings the Supabase REST API to prevent auto-pause.
                  Set the interval to 12-24 hours to stay well within the 7-day window.
                </span>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Supabase Project URL</span>
                  <span className="label-text-alt">Your Supabase project endpoint</span>
                </label>
                <input
                  type="url"
                  placeholder="https://<project-ref>.supabase.co"
                  className="input input-bordered"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">API Key</span>
                  <span className="label-text-alt">Supabase anon key or service_role key</span>
                </label>
                <input
                  type="password"
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                  className="input input-bordered"
                  value={(() => {
                    try {
                      const h = JSON.parse(formData.headers || '{}');
                      return h.apikey || '';
                    } catch { return formData.headers || ''; }
                  })()}
                  onChange={(e) => setFormData({ ...formData, headers: JSON.stringify({ apikey: e.target.value, Authorization: `Bearer ${e.target.value}` }) })}
                  required
                />
              </div>
            </>
          )}

          {/* Expected Status Field (for HTTP monitors) */}
          {(formData.type === 'static' || formData.type === 'functional' || formData.type === 'browser') && (
            <div className="form-control">
              <label className="label">
                <span className="label-text">Expected Status Code</span>
              </label>
              <input
                type="number"
                min="100"
                max="599"
                className="input input-bordered"
                value={formData.expected_status}
                onChange={(e) => setFormData({ ...formData, expected_status: parseInt(e.target.value) })}
                required
              />
            </div>
          )}

          {/* Alert Priority */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Alert Priority</span>
              <span className="label-text-alt">Controls re-alert frequency when the service stays down</span>
            </label>
            <select
              className="select select-bordered"
              value={formData.priority || 'medium'}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <option value="low">Low — alert once, no repeat</option>
              <option value="medium">Medium — re-alert every 30 min (default)</option>
              <option value="high">High — re-alert every 10 min</option>
              <option value="critical">Critical — re-alert every 3 min, all channels</option>
            </select>
          </div>

          {/* Advanced Options Toggle */}
          <div className="form-control">
            <label className="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                checked={showAdvancedOptions}
                onChange={(e) => setShowAdvancedOptions(e.target.checked)}
                className="checkbox checkbox-primary"
              />
              <span className="label-text">Show Advanced Options</span>
            </label>
          </div>

          {/* Advanced Options */}
          {showAdvancedOptions && (
            <>
              {/* Custom Headers (for HTTP monitors) */}
              {(formData.type === 'static' || formData.type === 'functional' || formData.type === 'browser') && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Custom Headers (JSON)</span>
                    <span className="label-text-alt">Optional: {"{\"Authorization\": \"Bearer token\", \"User-Agent\": \"Custom Agent\"}"}</span>
                  </label>
                  <textarea
                    placeholder='{"Authorization": "Bearer your-token", "Content-Type": "application/json"}'
                    className="textarea textarea-bordered"
                    value={formData.headers}
                    onChange={(e) => setFormData({ ...formData, headers: e.target.value })}
                    rows={3}
                  />
                </div>
              )}

              {/* Content Validation Fields (for functional monitors) */}
              {(formData.type === 'functional' || formData.type === 'browser') && (
                <>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Content Type</span>
                      <span className="label-text-alt">How to validate the response content</span>
                    </label>
                    <select
                      className="select select-bordered"
                      value={formData.content_type}
                      onChange={(e) => setFormData({ ...formData, content_type: e.target.value as 'text' | 'json' | 'regex' })}
                    >
                      <option value="text">Text (contains keyword)</option>
                      <option value="json">JSON (contains structure)</option>
                      <option value="regex">Regex (matches pattern)</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Expected Content</span>
                      <span className="label-text-alt">
                        {formData.content_type === 'text' && 'Text or keyword to find in response'}
                        {formData.content_type === 'json' && 'JSON structure to validate (e.g., {"status": "ok"})'}
                        {formData.content_type === 'regex' && 'Regex pattern to match (e.g., success|ok)'}
                      </span>
                    </label>
                    <textarea
                      placeholder={
                        formData.content_type === 'text' ? 'Enter text or keyword to find...' :
                        formData.content_type === 'json' ? '{"status": "ok", "data": {...}}' :
                        'success|ok|working'
                      }
                      className="textarea textarea-bordered"
                      value={formData.expected_content}
                      onChange={(e) => setFormData({ ...formData, expected_content: e.target.value })}
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* SSL-specific fields */}
              {formData.type === 'ssl' && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Alert Days Before Expiry</span>
                    <span className="label-text-alt">How many days before SSL expiry to alert</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    className="input input-bordered"
                    value={formData.ssl_check_days || 7}
                    onChange={(e) => setFormData({ ...formData, ssl_check_days: parseInt(e.target.value) })}
                    placeholder="7"
                  />
                </div>
              )}

              {/* Interval and Timeout */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Interval (seconds)</span>
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="86400"
                    className="input input-bordered"
                    value={Math.round((formData.interval ?? 10000) / 1000)}
                    onChange={(e) => setFormData({ ...formData, interval: parseInt(e.target.value) * 1000 })}
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Timeout (seconds)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    className="input input-bordered"
                    value={Math.round((formData.timeout || 10000) / 1000)}
                    onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) * 1000 })}
                    required
                  />
                </div>
              </div>
            </>
          )}

          {/* Basic Interval and Timeout (when advanced options are hidden) */}
          {!showAdvancedOptions && (
            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Check Every</span>
                </label>
                <select
                  className="select select-bordered"
                  value={Math.round((formData.interval ?? 60000) / 1000)}
                  onChange={(e) => setFormData({ ...formData, interval: parseInt(e.target.value) * 1000 })}
                >
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes</option>
                  <option value={600}>10 minutes</option>
                  <option value={1800}>30 minutes</option>
                  <option value={3600}>1 hour</option>
                  <option value={21600}>6 hours</option>
                  <option value={43200}>12 hours</option>
                  <option value={86400}>24 hours</option>
                </select>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Timeout</span>
                </label>
                <select
                  className="select select-bordered"
                  value={Math.round((formData.timeout || 10000) / 1000)}
                  onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) * 1000 })}
                >
                  <option value={5}>5 seconds</option>
                  <option value={10}>10 seconds</option>
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                </select>
              </div>
            </div>
          )}

          <div className="modal-action">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Saving...
                </>
              ) : (
                monitor ? 'Update Monitor' : 'Add Monitor'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MonitorModal;
